import React, { useEffect, useRef, useState } from 'react';
import { isGsiInitialized } from '../hooks/useAuth';

interface LoginPageProps {
  onLogin: () => void;
  loading: boolean;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onLogin, loading }) => {
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const [googleButtonRendered, setGoogleButtonRendered] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  // Rendert den offiziellen "Über Google anmelden"-Button, sobald die Google
  // Identity Services Bibliothek geladen und initialisiert ist. Der frühere
  // programmatische One-Tap-Aufruf (google.accounts.id.prompt) wird von
  // Browsern unter FedCM stillschweigend unterdrückt — der gerenderte Button
  // ist davon nicht betroffen.
  useEffect(() => {
    if (loading) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 50; // 50 x 100ms = 5 Sekunden

    const interval = setInterval(() => {
      attempts += 1;

      if (
        window.google?.accounts?.id &&
        isGsiInitialized() &&
        googleButtonRef.current
      ) {
        clearInterval(interval);
        try {
          window.google.accounts.id.renderButton(googleButtonRef.current, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            locale: 'de',
            width: 220
          });
          setGoogleButtonRendered(true);
        } catch (error) {
          console.error('Error rendering Google Sign-In button:', error);
          setShowFallback(true);
        }
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        setShowFallback(true);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [loading]);

  const loginContainerStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    margin: '0',
    fontFamily: 'inherit',
    backgroundColor: '#ffffff'
  };

  const loginContentStyle = {
    textAlign: 'center' as const
  };

  const logoStyle = {
    height: '80px',
    marginBottom: '32px'
  };

  const titleStyle = {
    fontSize: '24px',
    fontWeight: '500',
    color: '#111827',
    marginBottom: '16px',
    margin: '0 0 16px 0'
  };

  const subtitleStyle = {
    color: '#6B7280',
    marginBottom: '32px',
    margin: '0 0 32px 0'
  };

  const buttonStyle = {
    padding: '12px 24px',
    backgroundColor: '#2563EB',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontFamily: 'inherit'
  };

  const googleButtonContainerStyle = {
    display: 'flex',
    justifyContent: 'center'
  };

  if (loading) {
    return (
      <div style={loginContainerStyle}>
        <div>Wird geladen...</div>
      </div>
    );
  }

  return (
    <div style={loginContainerStyle}>
      <div style={loginContentStyle}>
        <div>
          <img 
            src="https://xtax.de/XTAX.png" 
            alt="XTAX Logo" 
            style={logoStyle}
          />
        </div>
        
        <h1 style={titleStyle}>
          Intranet
        </h1>
        
        <p style={subtitleStyle}>
          Melden Sie sich mit Ihrem XTAX Konto an
        </p>
        
        <div ref={googleButtonRef} style={googleButtonContainerStyle} />

        {showFallback && !googleButtonRendered && (
          <button
            onClick={onLogin}
            style={buttonStyle}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1D4ED8'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
          >
            Anmelden
          </button>
        )}
      </div>
    </div>
  );
};
